import React, { Fragment, useEffect, useMemo, useState }  from 'react';
import Swal from 'sweetalert2';

import { useAuth } from '../../Context/Context';

import SideNav from '../../Components/SideNav';
import Loader from '../../Components/Loader';
import PanelEdicion from '../../Components/PanelEdicion';
import PreviewSitio from '../../Components/PreviewSitio';
import SeccionPanel from '../../Components/SeccionPanel';

import usePanelEdicion, { confirmarDescartar } from '../../Hooks/usePanelEdicion';
import usePreviewSitio from '../../Hooks/usePreviewSitio';

import BanersHelper from '../../Helpers/Baners';
import { recorrerPosiciones } from '../../Helpers/posiciones';

// Cada tipo de banner se pinta en su propia franja del header; `modo` es el valor de la configuración que la muestra
const TIPOS = {
    1: { modo: 'generales', nombre: 'Banner general (marquesina)', ubicacion: 'Marquesina del header, en todas las páginas del sitio.' },
    2: { modo: 'oferta', nombre: 'Banner de oferta (con cuenta regresiva)', ubicacion: 'Franja de oferta del header, junto a la cuenta regresiva, en todas las páginas del sitio.' }
};

const MODOS = [
    { valor: 'apagado', etiqueta: 'Ninguno' },
    { valor: 'generales', etiqueta: 'Banners generales (marquesina)' },
    { valor: 'oferta', etiqueta: 'Banner de oferta (con cuenta regresiva)' }
];

const NUEVO = { titulo: '', tipo: 1, posicion: 1 };

const estiloAyuda = { color: "#757575", fontSize: "13px", margin: "-10px 0 10px" };
const estiloAviso = { background: "#fff8e1", color: "#8d6e00", fontSize: "13px", padding: "8px 10px", borderRadius: "4px" };

// La fecha se edita como AAAA-MM-DD; la API la guarda a las 23:59 UTC y el sitio le suma 6 h (23:59 del centro de México)
const extraerConfiguracion = configuracion => ({
    modo: configuracion?.modo ?? 'apagado',
    fecha: configuracion?.fecha ? String(configuracion.fecha).slice(0, 10) : '',
    color: configuracion?.color ?? '#FF007E'
});
const fechaApi = fecha => (fecha ? fecha + 'T23:59:00.000Z' : null);

// Las posiciones son por tipo: solo se recorren los banners del mismo tipo
const reacomodar = (lista, tipo, id, posicion) => [
    ...lista.filter(dato => dato.tipo !== tipo),
    ...recorrerPosiciones(lista.filter(dato => dato.tipo === tipo), 'posicion', id, posicion)
];

const Baners = () => {
    const { editar, setEditar, index, setIndex, data, setData, recargar } = useAuth();

    const [baner, setBaner] = useState({ ...editar });
    const [nuevo, setNuevo] = useState(NUEVO);
    const [seccionActiva, setSeccionActiva] = useState(null);
    const [version, setVersion] = useState(0);
    const [configuracionGuardada, setConfiguracionGuardada] = useState(null);
    const [configuracion, setConfiguracion] = useState(extraerConfiguracion(null));
    const [subiendo, setSubiendo] = useState(false);

    useEffect(() => {
        obtenerDatos();
    }, []);

    useEffect(() => {
        setBaner({ ...editar });
    }, [editar]);

    const obtenerDatos = async () => {
        const [resBaners, resConfiguracion] = await Promise.all([
            BanersHelper.obtenerBaners(),
            BanersHelper.obtenerConfiguracionBaners()
        ]);
        if (resBaners.success) {
            setData(resBaners.data);
        } else {
            Swal.fire('Error!', 'No se pudieron obtener los banners, codigo: ' + resBaners.code, 'error');
        }
        if (resConfiguracion.success) {
            const datos = extraerConfiguracion(resConfiguracion.data);
            setConfiguracionGuardada(datos);
            setConfiguracion(datos);
        } else {
            Swal.fire('Error!', 'No se pudo obtener la configuración de los banners, codigo: ' + resConfiguracion.code, 'error');
        }
    }

    const totalTipo = tipo => data.filter(dato => dato.tipo === tipo).length;
    // Al agregar hay una posición más: el nuevo puede ir al final
    const maximaPosicion = (tipo, esNuevo) => totalTipo(tipo) + (esNuevo ? 1 : 0);

    const hayCambiosEditar = Boolean(editar._id) && JSON.stringify(baner) !== JSON.stringify(editar);
    const hayCambiosNuevo = Boolean(nuevo.titulo);
    const hayCambiosConfiguracion = Boolean(configuracionGuardada) && JSON.stringify(configuracion) !== JSON.stringify(configuracionGuardada);
    const hayCambios = hayCambiosEditar || hayCambiosNuevo || hayCambiosConfiguracion;

    // cambiarBaner modifica `data` en vivo para la vista previa; `editar` conserva el original
    const limpiarBorradores = () => {
        if (index !== false) setData(data => data.map((dato, i) => (i === index ? editar : dato)));
        setEditar({});
        setIndex(false);
        setNuevo(NUEVO);
        if (configuracionGuardada) setConfiguracion(configuracionGuardada);
        setSeccionActiva(null);
        setVersion(version => version + 1);
    }

    const panel = usePanelEdicion({ hayCambios, onCerrar: limpiarBorradores });

    const abrirPanel = async (nombre, alAbrir) => {
        if (hayCambios && !(await confirmarDescartar())) return false;
        limpiarBorradores();
        alAbrir?.();
        panel.abrir(nombre);
        return true;
    }

    const abrirAgregar = () => abrirPanel('agregar', () => setNuevo({ ...NUEVO, posicion: totalTipo(NUEVO.tipo) + 1 }));

    const seleccionar = async ({ id }) => {
        if (id === 'configuracion' || id === 'fecha') {
            if (panel.abierto !== 'configuracion' && !(await abrirPanel('configuracion'))) return;
            if (id === 'fecha') {
                setSeccionActiva('oferta');
                // El formulario se monta al abrir el panel; se espera un ciclo para que el input exista
                setTimeout(() => document.getElementById('fecha')?.focus(), 100);
            }
            return;
        }
        const indice = data.findIndex(dato => dato._id === id);
        if (indice < 0 || (panel.abierto === 'editar' && indice === index)) return;
        abrirPanel('editar', () => {
            setEditar(data[indice]);
            setIndex(indice);
        });
    }

    const datosPreview = useMemo(() => {
        if (!configuracionGuardada) return null;
        let baners = data;
        if (nuevo.titulo) {
            baners = reacomodar([...data, { ...nuevo, _id: 'nuevo' }], nuevo.tipo, 'nuevo', nuevo.posicion);
        } else if (editar._id) {
            baners = reacomodar(data, editar.tipo, editar._id, baner.posicion);
        }
        // Con un banner abierto, el sitio muestra su franja aunque no sea la activa
        const tipoAbierto = panel.abierto === 'agregar' ? nuevo.tipo : panel.abierto === 'editar' ? editar.tipo : null;
        return {
            baners,
            configuracion: { ...configuracion, fecha: fechaApi(configuracion.fecha) },
            ver: TIPOS[tipoAbierto]?.modo ?? null
        };
    }, [data, nuevo, editar, baner.posicion, configuracion, configuracionGuardada, panel.abierto]);

    // seccion=header: en preview el sitio pinta solo el header
    const { iframeRef, src } = usePreviewSitio({
        page: 'baners',
        ruta: '/?seccion=header',
        data: datosPreview,
        onSelect: seleccionar
    });

    const cambiarBaner = cambios => {
        setBaner(baner => ({ ...baner, ...cambios }));
        setData(data => data.map((dato, i) => (i === index ? { ...dato, ...cambios } : dato)));
    }

    const cambiarNuevo = cambios => setNuevo(nuevo => ({ ...nuevo, ...cambios }));

    const cambiarConfiguracion = cambios => setConfiguracion(configuracion => ({ ...configuracion, ...cambios }));

    const errores = (borrador, maximo) => {
        if (!borrador.titulo?.trim()) return 'Faltan datos por llenar: Texto';
        if (!Number.isInteger(borrador.posicion) || borrador.posicion < 1 || borrador.posicion > maximo) {
            return 'La posición debe ser un número entre 1 y ' + maximo;
        }
        return null;
    }

    const agregar = async e => {
        e.preventDefault();
        const error = errores(nuevo, maximaPosicion(nuevo.tipo, true));
        if (error) return Swal.fire('Error!', error, 'error');
        setSubiendo(true);
        const res = await BanersHelper.agregarBaner({ ...nuevo, titulo: nuevo.titulo.trim() });
        if (res?.success) return recargar();
        setSubiendo(false);
    }

    const actualizar = async e => {
        e.preventDefault();
        const error = errores(baner, maximaPosicion(editar.tipo, false));
        if (error) return Swal.fire('Error!', error, 'error');
        setSubiendo(true);
        const res = await BanersHelper.editarBaner({ ...baner, titulo: baner.titulo.trim() }, editar._id);
        if (res?.success) return recargar();
        setSubiendo(false);
    }

    const eliminar = async () => {
        const ultimoActivo = TIPOS[editar.tipo].modo === configuracionGuardada?.modo && totalTipo(editar.tipo) === 1;
        const { isConfirmed } = await Swal.fire({
            icon: 'question',
            titleText: '¿Eliminar el banner "' + editar.titulo + '"?',
            text: ultimoActivo ? 'Es el último de la franja activa: el header quedará sin banner.' : undefined,
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });
        if (!isConfirmed) return;
        setSubiendo(true);
        const res = await BanersHelper.eliminarBaner(editar._id);
        if (res?.success) return recargar();
        setSubiendo(false);
    }

    const guardarConfiguracion = async e => {
        e.preventDefault();
        if (configuracion.modo === 'oferta' && !configuracion.fecha) return Swal.fire('Error!', 'Faltan datos por llenar: Fin de la oferta', 'error');
        if (!/^#[0-9a-fA-F]{6}$/.test(configuracion.color)) return Swal.fire('Error!', 'El color no es válido', 'error');
        setSubiendo(true);
        const res = await BanersHelper.editarConfiguracionBaner({ ...configuracion, fecha: fechaApi(configuracion.fecha) });
        if (res?.success) return recargar();
        setSubiendo(false);
    }

    const descartarConfiguracion = async () => {
        if (await confirmarDescartar()) setConfiguracion(configuracionGuardada);
    }

    const seccion = clave => ({ id: clave, activa: seccionActiva === clave, onFocus: () => setSeccionActiva(clave) });

    const camposBaner = (borrador, cambiar, prefijo) => {
        const id = campo => prefijo + campo;
        const esNuevo = prefijo === 'nuevo_';
        // Al editar, el tipo sale de `editar`: el borrador se copia en un useEffect y el primer render aún llega vacío
        const tipoBaner = esNuevo ? borrador.tipo : editar.tipo;
        const tipo = TIPOS[tipoBaner];
        const maximo = maximaPosicion(tipoBaner, esNuevo);

        return (
            <Fragment>
                {configuracionGuardada && tipo.modo !== configuracionGuardada.modo && (
                    <p style={{ ...estiloAyuda, margin: "0 0 10px" }}>Esta franja no se muestra en el sitio ahora (cámbialo en Configuración).</p>
                )}
                <SeccionPanel numero={1} titulo='Texto' ubicacion={tipo.ubicacion} {...seccion('texto')}>
                    {esNuevo ? (
                        <Fragment>
                            <label htmlFor={id('tipo')}>Tipo</label>
                            <select
                                id={id('tipo')}
                                className='browser-default'
                                value={borrador.tipo}
                                onChange={e => {
                                    const tipoNuevo = Number(e.target.value);
                                    cambiar({ tipo: tipoNuevo, posicion: totalTipo(tipoNuevo) + 1 });
                                }}
                                style={{marginBottom:"20px"}}
                            >
                                {Object.entries(TIPOS).map(([valor, { nombre }]) => (
                                    <option key={valor} value={valor}>{nombre}</option>
                                ))}
                            </select>
                        </Fragment>
                    ) : (
                        <p style={{ ...estiloAyuda, margin: "0 0 10px" }}>{tipo.nombre}. Para cambiarlo de franja, elimínalo y agrégalo en la otra.</p>
                    )}
                    <div className="input-field">
                        <input onChange={e => cambiar({ titulo: e.target.value })} value={borrador.titulo ?? ''} id={id('titulo')} type="text" className="validate" maxLength={200} />
                        <label className='active' htmlFor={id('titulo')}>Texto</label>
                    </div>
                    <p style={estiloAyuda}>Se muestra en una sola línea.</p>
                </SeccionPanel>

                <SeccionPanel numero={2} titulo='Posición' ubicacion='Orden dentro de su franja.' {...seccion('posicion')}>
                    <div className="input-field">
                        <input
                            onChange={e => cambiar({ posicion: e.target.value === '' ? '' : Number(e.target.value) })}
                            value={borrador.posicion ?? 1}
                            id={id('posicion')}
                            type="number"
                            min="1"
                            max={maximo}
                            className="validate"
                        />
                        <label className='active' htmlFor={id('posicion')}>Posición</label>
                    </div>
                    <p style={estiloAyuda}>1 = primero de la franja (de 1 a {maximo}). Al cambiarla, los demás banners se recorren.</p>
                </SeccionPanel>
            </Fragment>
        );
    }

    const { modo } = configuracion;
    const tipoDelModo = Object.keys(TIPOS).map(Number).find(tipo => TIPOS[tipo].modo === modo);
    const fechaPasada = modo === 'oferta' && configuracion.fecha && new Date(fechaApi(configuracion.fecha)).getTime() + 21600000 < Date.now();

    return (
        <Fragment>
            <SideNav />
            {subiendo && (
                <div style={{width:"100%", height:"100vh", position:"fixed", top:"0", background:"#00000059", zIndex:"9999"}}>
                    <Loader />
                </div>
            )}
            <div className='row'>
                <PanelEdicion
                    abierto={panel.abierto === 'agregar'}
                    titulo='Agregar banner'
                    ancho={panel.ancho}
                    onArrastre={panel.iniciarArrastre}
                    onCerrar={() => panel.cerrar()}
                    hayCambios={hayCambiosNuevo}
                    pie={
                        <button type='submit' form='form_agregar_baner' className='boton_admin' disabled={!hayCambiosNuevo}>
                            Agregar banner
                        </button>
                    }
                >
                    {panel.abierto === 'agregar' && (
                        <form id='form_agregar_baner' key={version} onSubmit={agregar}>
                            {camposBaner(nuevo, cambiarNuevo, 'nuevo_')}
                        </form>
                    )}
                </PanelEdicion>
                <PanelEdicion
                    abierto={panel.abierto === 'editar'}
                    titulo='Editar banner'
                    ancho={panel.ancho}
                    onArrastre={panel.iniciarArrastre}
                    onCerrar={() => panel.cerrar()}
                    hayCambios={hayCambiosEditar}
                    pie={
                        <Fragment>
                            <button type='button' className='boton_admin boton_admin_peligro' onClick={eliminar}>
                                Eliminar
                            </button>
                            <button type='submit' form='form_editar_baner' className='boton_admin' disabled={!hayCambiosEditar}>
                                Guardar cambios
                            </button>
                        </Fragment>
                    }
                >
                    {panel.abierto === 'editar' && editar._id && (
                        <form id='form_editar_baner' key={version} onSubmit={actualizar}>
                            {camposBaner(baner, cambiarBaner, 'editar_')}
                        </form>
                    )}
                </PanelEdicion>
                <PanelEdicion
                    abierto={panel.abierto === 'configuracion'}
                    titulo='Configuración de banners'
                    ancho={panel.ancho}
                    onArrastre={panel.iniciarArrastre}
                    onCerrar={() => panel.cerrar()}
                    hayCambios={hayCambiosConfiguracion}
                    pie={
                        <Fragment>
                            <button type='button' className='boton_admin boton_admin_secundario' onClick={descartarConfiguracion} disabled={!hayCambiosConfiguracion}>
                                Descartar
                            </button>
                            <button type='submit' form='form_configuracion_baner' className='boton_admin' disabled={!hayCambiosConfiguracion}>
                                Guardar cambios
                            </button>
                        </Fragment>
                    }
                >
                    {panel.abierto === 'configuracion' && configuracionGuardada && (
                        <form id='form_configuracion_baner' key={version} onSubmit={guardarConfiguracion}>
                            <SeccionPanel numero={1} titulo='Banner visible' ubicacion='Debajo del menú del sitio, en todas las páginas.' {...seccion('modo')}>
                                <label htmlFor='modo'>Qué se muestra</label>
                                <select
                                    id='modo'
                                    className='browser-default'
                                    value={modo}
                                    onChange={e => cambiarConfiguracion({ modo: e.target.value })}
                                    style={{marginBottom:"20px"}}
                                >
                                    {MODOS.map(({ valor, etiqueta }) => (
                                        <option key={valor} value={valor}>{etiqueta}</option>
                                    ))}
                                </select>
                                {tipoDelModo && totalTipo(tipoDelModo) === 0 && (
                                    <p style={estiloAviso}>No hay banners de este tipo; el header no mostrará nada.</p>
                                )}
                            </SeccionPanel>

                            {modo === 'oferta' && (
                                <SeccionPanel numero={2} titulo='Fin de la oferta' ubicacion='Cuenta regresiva de la franja de oferta.' {...seccion('oferta')}>
                                    <div className="input-field">
                                        <input onChange={e => cambiarConfiguracion({ fecha: e.target.value })} value={configuracion.fecha} id='fecha' type="date" className="validate" />
                                        <label className='active' htmlFor='fecha'>Fecha</label>
                                    </div>
                                    <p style={estiloAyuda}>La cuenta regresiva termina ese día a las 23:59 (hora del centro de México).</p>
                                    {fechaPasada && (
                                        <p style={estiloAviso}>La fecha ya pasó: el sitio mostrará que la cuenta regresiva terminó.</p>
                                    )}
                                </SeccionPanel>
                            )}

                            <SeccionPanel numero={modo === 'oferta' ? 3 : 2} titulo='Color' ubicacion='Fondo de la franja del banner.' {...seccion('color')}>
                                <input
                                    onChange={e => cambiarConfiguracion({ color: e.target.value })}
                                    value={configuracion.color}
                                    id='color'
                                    type="color"
                                    style={{width:"80px", height:"40px", padding:"0", border:"none", cursor:"pointer"}}
                                />
                                <p style={{ ...estiloAyuda, margin: "5px 0 10px" }}>También se usa en la etiqueta y los precios de las ofertas relámpago de los cursos.</p>
                            </SeccionPanel>
                        </form>
                    )}
                </PanelEdicion>
                <PreviewSitio
                    iframeRef={iframeRef}
                    src={src}
                    titulo='Vista previa del header del sitio'
                    margen={panel.abierto ? panel.ancho : 0}
                    encabezado='Banners'
                    ayuda='Haz clic en un banner para editarlo, o en la línea gris para cambiar cuál se muestra'
                    acciones={
                        <Fragment>
                            <button type='button' className='boton_admin boton_admin_secundario' onClick={() => abrirPanel('configuracion')}>
                                Configuración
                            </button>
                            <button type='button' className='boton_admin' onClick={abrirAgregar}>
                                + Agregar banner
                            </button>
                        </Fragment>
                    }
                />
            </div>
        </Fragment>
    );
}

export default Baners;
